/**
 * Ritual of Entry
 *
 * A 10–15 second cognitive threshold precedes interaction.
 * This reorients thinking before engagement.
 *
 * Sequence:
 * 0–2s:   Black. Nothing.
 * 2–4s:   First point appears.
 * 4–8s:   Points emerge from center outward.
 * 8–11s:  Motion begins.
 * 11–13s: Depth reveals (parallax activates).
 * 13–15s: Cursor appears. User can interact.
 */

import { useAuraStore } from '../state/store';

const TOTAL_DURATION = 14000; // ms
const SKIP_DURATION = 800; // Fast fade if skipping

export class EntryRitual {
  private startTime: number = 0;
  private started: boolean = false;
  private skipping: boolean = false;
  private overlay: HTMLElement | null;
  private modeSwitcher: HTMLElement | null;
  private arLogo: HTMLElement | null;

  constructor(options?: { deferAutoSkip?: boolean }) {
    this.overlay = document.getElementById('entry-overlay');
    this.modeSwitcher = document.getElementById('mode-switcher');
    this.arLogo = document.getElementById('ar-logo');

    // Check if we should skip (return visitor)
    // When deferAutoSkip is true, we hold the black screen until release() is called
    // (used when waiting for live data — prevents synthetic flash)
    if (!options?.deferAutoSkip) {
      const hasVisited = localStorage.getItem('aura_entry_seen');
      if (hasVisited) {
        this.skip();
      }
    }
  }

  /**
   * Begin the entry ritual after a deferred wait (e.g. live data loaded).
   * Respects return-visitor skip. Safe to call multiple times.
   */
  release() {
    if (this.started) return;
    const hasVisited = localStorage.getItem('aura_entry_seen');
    if (hasVisited) {
      this.skip();
    } else {
      this.start();
    }
  }

  start() {
    if (this.started) return;
    this.started = true;
    this.startTime = performance.now();

    // Hide cursor during entry
    document.body.style.cursor = 'none';
  }

  skip() {
    this.skipping = true;
    this.started = true;
    this.startTime = performance.now() - (TOTAL_DURATION - SKIP_DURATION);
    if (this.overlay) this.overlay.classList.add('fading');
  }

  /**
   * Called every frame. Returns the entry progress (0–1).
   */
  update(): number {
    if (!this.started) return 0;

    const elapsed = performance.now() - this.startTime;
    const duration = this.skipping ? SKIP_DURATION + (TOTAL_DURATION - (TOTAL_DURATION - SKIP_DURATION)) : TOTAL_DURATION;

    // Eased progress
    const raw = Math.min(elapsed / TOTAL_DURATION, 1);

    // Custom easing: slow start, steady middle, gentle arrival
    const progress = raw < 0.15
      ? raw * raw * (1 / 0.15) * (1 / 0.15) * 0.15 // Slow ramp
      : raw < 0.85
        ? 0.15 + (raw - 0.15) * (0.7 / 0.7) // Linear middle
        : 0.85 + (1 - Math.pow(1 - (raw - 0.85) / 0.15, 2)) * 0.15; // Gentle finish

    const clampedProgress = Math.min(Math.max(progress, 0), 1);

    // Phase transitions
    if (raw > 0.13 && this.overlay) {
      // Start fading the black overlay
      this.overlay.style.opacity = String(1 - (raw - 0.13) / 0.2);
    }

    if (raw > 0.85) {
      // Show cursor
      document.body.style.cursor = 'default';
    }

    if (raw > 0.9 && this.modeSwitcher) {
      // Show mode switcher + logo
      this.modeSwitcher.classList.add('visible');
      this.arLogo?.classList.add('visible');
    }

    // Update store
    useAuraStore.getState().setEntryProgress(clampedProgress);

    // Completion
    if (raw >= 1) {
      this.complete();
      return 1;
    }

    return clampedProgress;
  }

  private complete() {
    useAuraStore.getState().setEntryCompleted();
    localStorage.setItem('aura_entry_seen', 'true');

    if (this.overlay) {
      this.overlay.style.opacity = '0';
      setTimeout(() => {
        if (this.overlay) this.overlay.style.display = 'none';
      }, 2000);
    }

    document.body.style.cursor = 'default';
    if (this.modeSwitcher) this.modeSwitcher.classList.add('visible');
    this.arLogo?.classList.add('visible');
  }
}
